import { useEffect, useState } from "react"
import { useParams } from "react-router"
import type { ServerFileEntry } from "@mc/types"
import { searchMods, getModVersions, listInstalledMods, installMod, deleteMod, type ModrinthSearchResult } from "../api/mods.js"
import { friendlyError } from "../api/errorMessages.js"
import { Spinner } from "../components/ui/Spinner.js"
import { Button } from "../components/ui/Button.js"
import { Input } from "../components/ui/Input.js"
import { useToast } from "../components/ui/Toast.js"
import { Coachmark } from "../components/onboarding/Coachmark.js"
import { useFirstVisit } from "../components/onboarding/useFirstVisit.js"
import { getHint } from "../components/onboarding/hints.js"

/** `/servers/[id]/mods` (FR seccion 19 item 9). Busqueda vía Modrinth (api.modrinth.com), instalación reusa Files (routes/mods.ts). */
export function ServerModsPage() {
  const { serverId } = useParams<{ serverId: string }>()
  const [query, setQuery] = useState("")
  const [results, setResults] = useState<ModrinthSearchResult[]>([])
  const [searching, setSearching] = useState(false)
  const [installingProject, setInstallingProject] = useState<string | null>(null)
  const [installed, setInstalled] = useState<ServerFileEntry[]>([])
  const [installedLoaded, setInstalledLoaded] = useState(false)
  const [deletingFile, setDeletingFile] = useState<string | null>(null)
  const toast = useToast()
  const [isFirstVisit, dismissFirstVisit] = useFirstVisit("server-mods")

  useEffect(() => {
    if (!serverId) return
    loadInstalled(serverId)
  }, [serverId])

  function loadInstalled(id: string) {
    listInstalledMods(id)
      .then(({ mods }) => {
        setInstalled(mods)
        setInstalledLoaded(true)
      })
      .catch(() => setInstalledLoaded(true))
  }

  async function handleSearch() {
    setSearching(true)
    try {
      const { results } = await searchMods(query)
      setResults(results)
    } catch (err) {
      toast.show(friendlyError(err).message, "error")
    } finally {
      setSearching(false)
    }
  }

  async function handleInstall(project: ModrinthSearchResult) {
    if (!serverId) return
    setInstallingProject(project.projectId)
    try {
      const { versions } = await getModVersions(project.projectId)
      const latest = versions[0]
      const primaryFile = latest?.files.find((f) => f.primary) ?? latest?.files[0]
      if (!primaryFile) {
        toast.show("Ese mod no tiene ninguna versión con archivo descargable.", "error")
        return
      }
      await installMod(serverId, primaryFile.url, primaryFile.filename)
      toast.show(`${project.title} instalado.`, "success")
      loadInstalled(serverId)
    } catch (err) {
      toast.show(friendlyError(err).message, "error")
    } finally {
      setInstallingProject(null)
    }
  }

  function handleDelete(entry: ServerFileEntry) {
    if (!serverId) return
    toast.confirmDestructive(
      `Se va a eliminar "${entry.name}".`,
      async () => {
        setDeletingFile(entry.name)
        try {
          await deleteMod(serverId, entry.name)
          setInstalled((prev) => prev.filter((m) => m.name !== entry.name))
          toast.show("Mod eliminado.", "success")
        } catch (err) {
          toast.show(friendlyError(err).message, "error")
        } finally {
          setDeletingFile(null)
        }
      },
      () => toast.show("Eliminación cancelada.", "info"),
    )
  }

  const content = (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-8)" }}>
      <section style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
        <h2 style={{ fontSize: "var(--text-2xl)" }}>Instalados</h2>
        {!installedLoaded ? (
          <Spinner size="sm" />
        ) : installed.length === 0 ? (
          <p style={{ color: "var(--text-muted)", fontSize: "var(--text-sm)" }}>Todavía no hay mods instalados.</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
            {installed.map((mod) => (
              <div
                key={mod.name}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "var(--space-3)",
                  padding: "var(--space-3)",
                  borderRadius: "var(--radius-md)",
                  border: "1px solid var(--border-default)",
                  background: "var(--bg-surface)",
                }}
              >
                <span style={{ fontSize: "var(--text-sm)" }}>{mod.name}</span>
                <Button size="sm" variant="destructive" loading={deletingFile === mod.name} onClick={() => handleDelete(mod)}>
                  Eliminar
                </Button>
              </div>
            ))}
          </div>
        )}
      </section>

      <section style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
        <h2 style={{ fontSize: "var(--text-2xl)" }}>Buscar en Modrinth</h2>
        <div style={{ display: "flex", alignItems: "flex-end", gap: "var(--space-2)", flexWrap: "wrap" }}>
          <div style={{ width: 320 }}>
            <Input
              label="Buscar mods"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSearch()
              }}
            />
          </div>
          <Button variant="secondary" loading={searching} disabled={!query.trim()} onClick={handleSearch}>
            Buscar
          </Button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
          {results.map((result) => (
            <div
              key={result.projectId}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "var(--space-3)",
                padding: "var(--space-3)",
                borderRadius: "var(--radius-md)",
                border: "1px solid var(--border-default)",
                background: "var(--bg-surface)",
                flexWrap: "wrap",
              }}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-1)", flex: 1, minWidth: 220 }}>
                <strong style={{ fontSize: "var(--text-sm)" }}>{result.title}</strong>
                <span style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>{result.description}</span>
              </div>
              <Button size="sm" loading={installingProject === result.projectId} onClick={() => handleInstall(result)}>
                Instalar
              </Button>
            </div>
          ))}
        </div>
      </section>
    </div>
  )

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
      <div>
        <h1 style={{ fontSize: "var(--text-3xl)" }}>Mods</h1>
        <p style={{ color: "var(--text-muted)" }}>Buscá e instalá mods desde Modrinth.</p>
      </div>
      {isFirstVisit ? (
        <Coachmark title={getHint("server-mods").title} description={getHint("server-mods").description} onDismiss={dismissFirstVisit}>
          {content}
        </Coachmark>
      ) : (
        content
      )}
    </div>
  )
}
